// ─── LOCAL FINANCIAL ENGINE ───────────────────────────────────────────────────
// Extracts transactions from PDF text, categorises them, and generates a full
// French financial analysis — zero Claude API calls required.

const MONTHS_FR = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
export const EXPENSE_KEYS_LOCAL = ["rent","food","transport","utilities","subscriptions","health","leisure","savings","other"];

// ─── KEYWORD CATEGORISER ─────────────────────────────────────────────────────
const MERCHANT_CATS = {
  income: ["VIREMENT SALAIRE","VIR SALAIRE","SALAIRE","PAIE ","REMUNERATION","TRAITEMENT ",
           "VIR EMPLOYEUR","APL ","CAF ","ASSEDIC","POLE EMPLOI","FRANCE TRAVAIL",
           "RETRAITE","PENSION","DIVIDENDE","LOYERS PERCUS"],
  rent: ["LOYER","VIR LOYER","AGENCE IMMO","SCI ","FONCIERE","SYNDIC","COPROPRIETE",
         "CREDIT LOGEMENT","PRET IMMOBILIER","MENSUALITE PRET","CREDIT FONCIER","HABITAT"],
  food: ["CARREFOUR","E.LECLERC","LECLERC","LIDL","ALDI","INTERMARCHE","MONOPRIX","CASINO ",
         "FRANPRIX","AUCHAN","SIMPLY","SUPER U","CORA ","BIOCOOP","NATURALIA","PICARD",
         "MCDO","MCDONALD","BURGER","KFC","SUBWAY","QUICK","PIZZA","SUSHI","NOODLE",
         "BOULANGERIE","PATISSERIE","BRIOCHE","BAGUETTE","PAUL ","ERIC KAYSER",
         "DELIVEROO","UBER EAT","JUST EAT","DOMINOS","MONOP"],
  transport: ["SNCF","RATP","NAVIGO","ILE-DE-FRANCE MOB","TRANSDEV","KEOLIS",
              "UBER ","BLABLACAR","OUIGO","FLIXBUS","EUROSTAR","THALYS",
              "TOTAL ENERGIE","TOTAL ACCESS","BP STATION","SHELL","ESSO","Q8","TOTAL FIN",
              "VINCI AUTOROUTES","SANEF","COFIROUTE","ASF ","APRR ","AUTOPAY",
              "FREE2MOVE","VELIB","VELIGO","BIRD ","TIER ","DOTT "],
  utilities: ["EDF ","ENGIE ","DIRECT ENERGIE","TOTALENERGIES","VATTENFALL",
              "VEOLIA","SUEZ ","SAUR ","LYONNAISE",
              "ORANGE ","SFR ","BOUYGUES TELECOM","FREE MOBILE","FREE TELECOM",
              "NUMERICABLE","LA POSTE MOBILE","SOSH ",
              "MAIF ","MACIF ","MMA ","AXA ","ALLIANZ ","GROUPAMA ","GMF ","MATMUT ",
              "ASSURANCE HABITATION","ASSURANCE AUTO","MUTUELLE ","MGEN"],
  subscriptions: ["NETFLIX","DISNEY PLUS","DISNEY+","AMAZON PRIME","PRIME VIDEO",
                  "SPOTIFY","DEEZER","APPLE MUSIC","APPLE ONE","ITUNES","APP STORE",
                  "GOOGLE ONE","GOOGLE STORAGE","YOUTUBE PREMIUM",
                  "MICROSOFT 365","OFFICE 365","ADOBE ",
                  "CANAL PLUS","CANAL+","BEINSPORTS","DAZN",
                  "BASIC FIT","NEONESS","KEEP COOL","FITLANE","YOURFIT","MOVING"],
  health: ["PHARMACIE","PHARMA ","DR ","DOCTEUR","CABINET MEDICAL","CABINET DR",
           "OPTICIEN","OPHTALMOLOG","OPTO ","DENTISTE","DENTAL","ORTHODONTISTE",
           "INFIRMIER","KINESITHERA","KINE ","PEDICURE","PODOLOGUE","LABORATOIRE",
           "HOPITAL","CLINIQUE","CHU ","CHR ","AP-HP",
           "HARMONIE MUTUELLE","ALAN ","MALAKOFF","PRO BTP","APRIL ","MGEN "],
  leisure: ["FNAC ","DARTY","BOULANGER","CDISCOUNT","AMAZON","RAKUTEN",
            "DECATHLON","GO SPORT","SPORT 2000","INTERSPORT",
            "ZARA ","H&M ","UNIQLO","PRIMARK","MANGO ","SHEIN","ASOS","ZALANDO","VINTED",
            "UGC ","GAUMONT","PATHE ","CGR ","CINEMA",
            "THEATRE","OPERA","CONCERT","MUSEE","EXPOSITION",
            "BOOKING","AIRBNB","HOTELS","NOVOTEL","IBIS ","ACCORHOTELS",
            "TICKETMASTER","FNACSPECTACLES"],
  savings: ["LIVRET A","LIVRET D","LDDS","LDD ","CEL ","PEL ","PEA ",
            "ASSURANCE VIE","CAPITALISATION","EPARGNE","VIR EPARGNE","VIREMENT EPARGNE",
            "FORTUNEO","BOURSORAMA EPARG","LINXEA","SPIRICA","GENERALI EPARG"],
};

export function categoriseDescription(desc) {
  if (!desc) return "other";
  const up = desc.toUpperCase().replace(/\s+/g, " ");
  for (const [cat, kws] of Object.entries(MERCHANT_CATS)) {
    if (kws.some(k => up.includes(k))) return cat === "income" ? "income" : cat;
  }
  return "other";
}

// ─── PDF TEXT → TRANSACTIONS ──────────────────────────────────────────────────
function parseDateStr(str) {
  const sep = str.includes("/") ? "/" : str.includes("-") ? "-" : ".";
  const parts = str.split(sep).map(Number);
  const now = new Date();
  if (parts.length === 3) {
    let d, mo, y;
    if (str.length >= 8 && parts[0] > 31) { [y, mo, d] = parts; }
    else { [d, mo, y] = parts; if (y < 100) y += 2000; }
    if (mo < 1 || mo > 12) return null;
    return { year: y, month: mo, day: d };
  }
  if (parts.length === 2) {
    const [d, mo] = parts;
    if (mo < 1 || mo > 12) return null;
    return { year: now.getFullYear(), month: mo, day: d };
  }
  return null;
}

export function parseTransactionsFromText(text) {
  const txs = [];
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  const dateRe = /^(\d{2}[\/\-.]\d{2}(?:[\/\-.]\d{2,4})?)\s+/;
  const isoRe  = /^(\d{4}[\/\-]\d{2}[\/\-]\d{2})\s+/;
  const amtRe  = /([-+]?\s*\d[\d\s]*[,\.]\d{2})\s*(?:€|EUR)?$/;
  const skipRe = /^(SOLDE|TOTAL|REPORT|DATE\s|LIBELLE|DEBIT|CREDIT|MONTANT|ANCIEN\s|NOUVEAU\s|PAGE\s)/i;

  for (const line of lines) {
    if (line.length < 8 || skipRe.test(line)) continue;
    let dateStr = null, rest = line;
    const isoM = line.match(isoRe);
    const dM   = isoM ? null : line.match(dateRe);
    if (isoM) { dateStr = isoM[1]; rest = line.slice(isoM[0].length); }
    else if (dM) { dateStr = dM[1]; rest = line.slice(dM[0].length); }
    else continue;

    const amtM = rest.match(amtRe);
    if (!amtM) continue;
    const amount = parseFloat(amtM[1].replace(/\s/g,"").replace(",","."));
    if (isNaN(amount) || amount === 0) continue;

    const description = rest.slice(0, rest.lastIndexOf(amtM[0])).trim();
    if (!description || description.length < 2) continue;

    const date = parseDateStr(dateStr);
    if (!date) continue;
    txs.push({ date, description, amount });
  }
  return txs;
}

// ─── GROUP TRANSACTIONS BY MONTH ──────────────────────────────────────────────
function emptyMonth(key, label) {
  return { month: key, label, income: 0, rent: 0, food: 0, transport: 0,
           utilities: 0, subscriptions: 0, health: 0, leisure: 0, savings: 0, other: 0 };
}

export function aggregateByMonth(txs) {
  const map = {};
  for (const tx of txs) {
    const key   = `${tx.date.year}-${String(tx.date.month).padStart(2,"0")}`;
    const label = `${MONTHS_FR[tx.date.month - 1]} ${tx.date.year}`;
    if (!map[key]) map[key] = emptyMonth(key, label);
    const cat = categoriseDescription(tx.description);
    if (tx.amount > 0 || cat === "income") {
      map[key].income += Math.abs(tx.amount);
    } else {
      const abs = Math.abs(tx.amount);
      if (EXPENSE_KEYS_LOCAL.includes(cat)) map[key][cat] += abs;
      else map[key].other += abs;
    }
  }
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
}

// ─── FRENCH TAX 2024 ──────────────────────────────────────────────────────────
const BRACKETS = [
  { limit: 11294, rate: 0 },
  { limit: 28797, rate: 0.11 },
  { limit: 82341, rate: 0.30 },
  { limit: 177106, rate: 0.41 },
  { limit: Infinity, rate: 0.45 },
];

function calcTax(annual, parts) {
  const q = annual / parts;
  let tax = 0, prev = 0;
  for (const { limit, rate } of BRACKETS) {
    if (q <= prev) break;
    tax += (Math.min(q, limit) - prev) * rate;
    prev = limit;
  }
  return Math.max(0, tax * parts);
}

function nbParts(profile) {
  const base = (profile.situation === "marié" || profile.situation === "pacsé") ? 2 : 1;
  const n = parseInt(profile.nb_enfants) || 0;
  const cp = n === 0 ? 0 : n === 1 ? 0.5 : n === 2 ? 1 : 1 + (n - 2);
  return base + cp;
}

// ─── ANALYSIS TEXT GENERATOR ──────────────────────────────────────────────────
const f = n => Math.round(n).toLocaleString("fr-FR");

export function generateLocalAnalysis(months, profile) {
  if (!months || months.length === 0)
    return { analysisText: "❌ Aucune donnée exploitable.", metrics: null };

  // Average across all months
  const avg = Object.fromEntries(
    ["income", ...EXPENSE_KEYS_LOCAL].map(k => [
      k, months.reduce((s, m) => s + (m[k] || 0), 0) / months.length,
    ])
  );
  const total    = EXPENSE_KEYS_LOCAL.reduce((s, k) => s + avg[k], 0);
  const balance  = avg.income - total;
  const explicit = avg.savings;
  const totSav   = explicit + Math.max(0, balance);
  const pct      = v => avg.income > 0 ? Math.round(v / avg.income * 100) : 0;

  const rentPct  = pct(avg.rent);
  const foodPct  = pct(avg.food);
  const savPct   = pct(totSav);
  const subsPct  = pct(avg.subscriptions);
  const leisPct  = pct(avg.leisure);

  // Tax
  const parts       = nbParts(profile);
  const annualInc   = avg.income * 12;
  const taxAmt      = calcTax(annualInc, parts);
  const effectRate  = annualInc > 0 ? (taxAmt / annualInc * 100) : 0;

  // Borrowing — French HCSF 35% rule, net of existing loan payments
  // "rent" captures mortgage repayments when profile says "emprunt_immobilier"
  // We also check "other" for detectable consumer/auto credit patterns (conservative: 0 if unknown)
  const mortgagePayment  = profile.proprietaire === "emprunt_immobilier" ? avg.rent : 0;
  const existingLoans    = Math.round(mortgagePayment); // add more sources when detectable
  const maxDebtPayment   = Math.round(avg.income * 0.35);
  const remainingCap     = Math.max(0, maxDebtPayment - existingLoans);
  const maxLoan          = Math.round(remainingCap * 171);

  // Score
  let score = 50;
  if (savPct >= 20) score += 15; else if (savPct >= 10) score += 7; else if (savPct < 0) score -= 15;
  if (rentPct > 0 && rentPct <= 25) score += 10; else if (rentPct <= 33) score += 5; else if (rentPct > 40) score -= 10;
  if (balance > 0) score += 10; else if (balance < 0) score -= 10;
  if (avg.income >= 3000) score += 5; if (avg.income >= 5000) score += 5;
  score = Math.min(95, Math.max(10, Math.round(score)));

  const scoreLabel  = score >= 80 ? "Excellente" : score >= 65 ? "Bonne" : score >= 50 ? "Correcte" : "À améliorer";
  const mRange      = months.length > 1 ? `${months[0].label} — ${months[months.length-1].label}` : months[0].label;

  let t = "";

  t += `## 📊 Score de santé financière\n`;
  t += `Score global : **${score}/100** — analyse sur **${months.length} mois** (${mRange}).\n`;
  t += `Situation **${scoreLabel}** · Solde mensuel moyen **${balance >= 0 ? "+" : ""}${f(balance)} €** · Taux d'épargne total **${savPct}%**.\n\n`;

  t += `## 📐 Analyse des ratios clés\n`;
  t += `Revenus nets moyens : **${f(avg.income)} €/mois** (${f(annualInc)} €/an estimé)\n`;
  if (avg.rent > 0) t += `- **Logement** : ${f(avg.rent)} € (${rentPct}%) — ${rentPct <= 25 ? "✅ Excellent" : rentPct <= 33 ? "✅ Correct" : "⚠️ Élevé"} · standard max 33%\n`;
  if (avg.food > 0) t += `- **Alimentation** : ${f(avg.food)} € (${foodPct}%) — ${foodPct <= 12 ? "✅ Maîtrisé" : foodPct <= 18 ? "✅ Dans la moyenne" : "⚠️ Élevé"} · standard 10-15%\n`;
  if (avg.transport > 0) t += `- **Transport** : ${f(avg.transport)} € (${pct(avg.transport)}%)\n`;
  if (avg.utilities > 0) t += `- **Charges & Télécom** : ${f(avg.utilities)} € (${pct(avg.utilities)}%)\n`;
  if (avg.subscriptions > 0) t += `- **Abonnements** : ${f(avg.subscriptions)} € (${subsPct}%) — ${subsPct > 5 ? "⚠️ À rationaliser" : "✅ OK"}\n`;
  if (avg.leisure > 0) t += `- **Loisirs** : ${f(avg.leisure)} € (${leisPct}%) — ${leisPct <= 10 ? "✅ Correct" : "⚠️ Au-dessus du standard (max 10%)"}\n`;
  t += `- **Épargne totale** : **${f(totSav)} €/mois (${savPct}%)** — ${savPct >= 20 ? "✅ Excellent" : savPct >= 10 ? "⚠️ Insuffisant, viser 20%" : "🔴 Très insuffisant"}\n\n`;

  t += `## 🏦 Capacité d'emprunt\n`;
  t += `Plafond légal d'endettement (35%) : **${f(maxDebtPayment)} €/mois**\n`;
  if (existingLoans > 0) {
    t += `- Crédits en cours détectés (prêt immobilier) : **− ${f(existingLoans)} €/mois**\n`;
    t += `- **Capacité résiduelle pour un nouvel emprunt : ${f(remainingCap)} €/mois**\n`;
  } else {
    t += `- Aucun crédit en cours détecté — capacité disponible en totalité\n`;
    t += `- **Mensualité maximale pour un nouvel emprunt : ${f(maxDebtPayment)} €/mois**\n`;
  }
  if (remainingCap === 0) {
    t += `- ⚠️ Plafond d'endettement atteint — aucun nouvel emprunt possible sans rembourser un crédit existant\n`;
  } else {
    t += `- Montant empruntable supplémentaire à 3,5% sur 20 ans : **${f(maxLoan)} €**\n`;
    if (maxLoan > 300000) t += `- Profil compatible avec un achat en zone tendue ou résidence principale confortable\n`;
    else if (maxLoan > 150000) t += `- Profil compatible avec un appartement 2-3 pièces en zone intermédiaire\n`;
    else if (maxLoan > 80000) t += `- Profil compatible avec un studio ou petit appartement en zone B/C\n`;
    else t += `- Augmenter revenus ou rembourser des crédits pour améliorer le profil emprunteur\n`;
  }
  t += `\n`;

  t += `## 💰 Optimisation fiscale\n`;
  t += `Revenu annuel estimé : **${f(annualInc)} €** · ${parts} part(s) fiscale(s)\n`;
  t += `Impôt estimé 2024 : **${f(Math.round(taxAmt))} €** · Taux effectif : **${effectRate.toFixed(1)}%**\n`;
  if (annualInc > 0 && taxAmt > 0) {
    const perCap  = Math.min(annualInc * 0.1, 32909);
    const perSave = Math.round(perCap * effectRate / 100);
    if (perSave > 200) t += `- **PER** : verser jusqu'à **${f(Math.round(perCap))} €/an** → économie fiscale estimée **${f(perSave)} €**\n`;
  }
  t += `- **Livret A** (3%, plafond 22 950 €) + **LDDS** (3%, plafond 12 000 €) : intérêts exonérés d'impôt — à maximiser en priorité\n`;
  if (profile.statut_pro === "indépendant") t += `- Statut indépendant : explorer loi Madelin, régime micro vs réel, cotisations TNS déductibles\n`;
  if (profile.situation === "marié" || profile.situation === "pacsé") t += `- Déclaration commune : vérifier si la déclaration séparée est plus avantageuse en cas de revenus très différents\n`;
  const enfants = parseInt(profile.nb_enfants) || 0;
  if (enfants > 0) t += `- ${enfants} enfant(s) à charge → quotient familial de ${parts} parts déjà appliqué\n`;
  t += `\n`;

  t += `## 📈 Stratégie d'investissement\n`;
  const free = Math.max(0, Math.round(balance));
  if (free > 0) {
    t += `Capacité mensuelle disponible : **${f(free)} €**\n`;
    t += `- **Court terme** : épargne de précaution **${f(Math.round(total * 3))} €** (3 mois de charges) → Livret A\n`;
    if (free >= 100) t += `- **Moyen terme (3-5 ans)** : **${f(Math.round(free * 0.3))} €/mois** → Assurance-vie (fonds euros + UC) ou SCPI\n`;
    if (free >= 150) t += `- **Long terme (>5 ans)** : **${f(Math.round(free * 0.5))} €/mois** → PEA (ETF MSCI World) ou PER pour défiscaliser\n`;
  } else {
    t += `Budget à l'équilibre ou déficitaire — priorité à dégager au moins **100 €/mois** avant d'investir.\n`;
    t += `- Identifier les postes compressibles (abonnements, loisirs)\n`;
  }
  t += `\n`;

  t += `## 💡 Points forts & alertes\n`;
  const strengths = [], alerts = [];
  if (balance > 0) strengths.push(`Cash-flow positif de **${f(balance)} €/mois** — gestion globale saine`);
  if (savPct >= 15) strengths.push(`Taux d'épargne de **${savPct}%** — au-dessus de la moyenne française`);
  if (rentPct > 0 && rentPct <= 30) strengths.push(`Logement maîtrisé à **${rentPct}%** du revenu`);
  if (avg.income >= 3000) strengths.push(`Revenus au-dessus du salaire médian (**${f(avg.income)} €/mois**)`);
  if (savPct < 10 && avg.income > 0) alerts.push(`Épargne de **${savPct}%** insuffisante — viser 20%, soit **${f(Math.round(avg.income * 0.20 - totSav))} €** de plus/mois`);
  if (rentPct > 35) alerts.push(`Logement à **${rentPct}%** des revenus — au-dessus du seuil de 33%`);
  if (balance < 0) alerts.push(`Budget déficitaire de **${f(Math.abs(balance))} €/mois** — non soutenable à long terme`);
  if (subsPct > 5) alerts.push(`Abonnements à **${subsPct}%** du revenu (**${f(avg.subscriptions)} €**) — passer en revue et résilier les non utilisés`);
  if (leisPct > 15) alerts.push(`Loisirs élevés à **${leisPct}%** du revenu — standard : max 10%`);
  strengths.slice(0, 3).forEach(s => { t += `- ✅ ${s}\n`; });
  alerts.slice(0, 3).forEach(a => { t += `- ⚠️ ${a}\n`; });
  t += `\n`;

  t += `## 🎯 Actions prioritaires cette semaine\n`;
  const actions = [];
  if (savPct < 20 && avg.income > 0) {
    const diff = Math.round(avg.income * 0.20 - totSav);
    if (diff > 0) actions.push(`Virement automatique de **${f(diff)} €/mois** supplémentaires vers Livret A dès réception du salaire`);
  }
  if (annualInc > 25000 && taxAmt > 500) {
    actions.push(`Ouvrir un PER et verser **${f(Math.round(Math.min(avg.income * 0.05, 300)))} €/mois** pour réduire l'impôt dès cette année`);
  }
  if (remainingCap > 0 && maxLoan > 100000 && profile.proprietaire === "locataire") {
    actions.push(`Contacter 2-3 banques pour simulation d'emprunt — capacité résiduelle **${f(remainingCap)} €/mois** → montant estimé **${f(maxLoan)} €**`);
  }
  if (avg.subscriptions > 50) actions.push(`Lister tous les abonnements et résilier les non utilisés — **${f(avg.subscriptions)} €/mois** en jeu`);
  if (actions.length < 3) {
    actions.push(`Automatiser l'épargne : virement programmé le jour du salaire, avant toute dépense`);
    actions.push(`Comparer les offres téléphone/internet — économie potentielle de 15-30 €/mois sans effort`);
  }
  actions.slice(0, 3).forEach((a, i) => { t += `- **${i+1}.** ${a}\n`; });

  return {
    analysisText: t,
    metrics: {
      months,
      latestMonth: months[months.length - 1],
      borrowing_capacity: { existing_loan_payments: existingLoans, max_debt_ratio_payment: maxDebtPayment, remaining_capacity: remainingCap, max_new_loan_amount: maxLoan, rate_pct: 3.5, duration_years: 20 },
      tax_estimate: { revenu_annuel_estime: Math.round(annualInc), impot_estime: Math.round(taxAmt), taux_effectif_pct: parseFloat(effectRate.toFixed(1)), nb_parts: parts, alerte_erreur: false },
    },
  };
}
